import { Card } from "antd";
import dayjs from "dayjs";
import test_img from "../../../images/32637972_7843794.jpg";
import { getMyProgress } from "../../utils/campaignStatus";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../../utils/categories";
import {
  getDeadlineOverlayText,
  isDeadlineSoon,
} from "../../utils/campaignDeadline";
import { getContentFormatOptions } from "../../utils/contentFormat";
import { parseApiAmount } from "../../utils/amountFormat";

const getRecruitmentPeriod = (campaign) => {
  const start = dayjs(campaign.start_at).format("YYYY-MM-DD");
  const end = dayjs(campaign.end_at).format("YYYY-MM-DD");
  return `${start} ~ ${end}`;
};

const getCampaignStatus = (status) => {
  const map = {
    active: "진행 중",
    finished: "종료",
    rejected: "반려",
    pending: "승인 대기 중",
    stopped: "중지",
  };
  return map[status] ?? "알 수 없음";
};

const STATUS_BADGE_CLASS = {
  pending: "bg-[#e3f2fd] text-[#1565c0]",
  active: "bg-[#e8f5e9] text-[#2e7d32]",
  finished: "bg-[#eceff1] text-[#546e7a]",
  rejected: "bg-[#ffebee] text-[#c62828]",
  stopped: "bg-red-100 text-red-800",
};

const DEFAULT_STATUS_BADGE_CLASS = "bg-[#f5f5f5] text-[#616161]";

export const CampaignCard = ({ campaign, variant, selected, onClick }) => {
  if (!campaign) return null;

  const payoutAmount =
    typeof campaign.payout === "object"
      ? parseApiAmount(campaign.payout)
      : Number(campaign.payout);

  const rewardLabel =
    `${payoutAmount.toLocaleString()} ${campaign.currency ?? ""}`.trim();

  const overlayDeadline = getDeadlineOverlayText(campaign.end_at);
  const deadlineUrgent = isDeadlineSoon(campaign.end_at);
  const periodLabel = getRecruitmentPeriod(campaign);

  const contentFormatOptions = getContentFormatOptions(campaign.content_format);

  const categoryStyle =
    CATEGORY_STYLES[campaign.campaign_category] || CATEGORY_STYLES.etc;

  const isMy = variant === "my-campaign";
  const isBrandManage = variant === "brand-manage";
  const my = getMyProgress(campaign);

  const statusBadgeClass =
    STATUS_BADGE_CLASS[campaign.status] ?? DEFAULT_STATUS_BADGE_CLASS;

  return (
    <Card
      hoverable
      key={campaign.id}
      onClick={onClick}
      styles={{ body: { padding: 0 } }}
      className={`overflow-hidden rounded-2xl border border-[#eef2f7] bg-white shadow-[0_2px_12px_rgba(15,23,42,0.06)]
      transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.1)]
      ${selected ? "ring-2 ring-[#93c5fd]" : ""}`}
      cover={
        <div className="relative h-[168px] overflow-hidden bg-[#f3f4f6] md:h-[192px]">
          <img
            src={campaign.thumbnail || test_img}
            alt=""
            className="h-full w-full object-cover"
          />

          {overlayDeadline && (
            <div className="absolute right-3 top-3">
              <span
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm ${
                  deadlineUrgent ? "bg-[#ff3e3e]" : "bg-[#1f2937]"
                }`}
              >
                {overlayDeadline}
              </span>
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4 p-5 md:p-6">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex shrink-0 items-center rounded-lg px-2.5 py-1 text-[12px] font-medium leading-none"
              style={categoryStyle}
            >
              {CATEGORY_MAPPING[campaign.campaign_category] || "기타"}
            </span>
            {isBrandManage && (
              <span
                className={`inline-flex shrink-0 items-center rounded-lg px-2.5 py-1 text-[12px] font-medium leading-none ${statusBadgeClass}`}
              >
                {getCampaignStatus(campaign.status)}
              </span>
            )}
          </div>

          <div
            className="line-clamp-2 text-[18px] font-bold leading-snug tracking-tight text-[#111827] md:text-[19px]"
            title={campaign.title}
          >
            {campaign.title}
          </div>

          {campaign.company_name ? (
            <div className="text-[11px] font-medium leading-snug text-[#9ca3af]">
              {campaign.company_name}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <span className="inline-flex items-center rounded-full text-[#414141] text-[12px] font-medium">
            모집인원 {campaign.recruit_count}명
          </span>

          {campaign.seeding_type === "paid" && (
            <span className="inline-flex items-center rounded-full text-[#414141] text-[12px] font-medium">
              리워드 {rewardLabel}
            </span>
          )}

          <div className="flex flex-wrap gap-2">
            {contentFormatOptions.map((option) => {
              const Icon = option.Icon;

              return (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 rounded-full text-[12px] font-medium text-[#414141]"
                >
                  {Icon ? (
                    <Icon
                      className={`text-[13px] shrink-0 ${
                        option.iconClassName ?? ""
                      }`}
                      aria-hidden
                    />
                  ) : null}
                  {option.label}
                </span>
              );
            })}
          </div>
        </div>

        {isMy && (
          <div className="flex items-center gap-2">
            <p className="text-[12px] font-medium">진행 상태 - </p>
            <p className={`text-[12px] font-medium ${my.className}`}>
              {my.text}
            </p>
          </div>
        )}

        <div className="text-[12px] font-medium">모집기간 {periodLabel}</div>
      </div>
    </Card>
  );
};
